import { useEffect, useState, useCallback, useRef } from "react";
import {
  X, Router, Plus, Trash2, Pencil, RefreshCw, Loader2, Activity, Bell,
  ArrowDownUp, Signal, Wifi, WifiOff, Save, MapPin, Maximize2, Radar,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useI18n } from "@/context/I18nContext";

const EMPTY = {
  name: "", host: "", snmp_port: 161, version: "v2c", manufacturer: "generic",
  poll_method: "snmp",
  ssh_user: "admin", ssh_port: 22, ssh_password: "", ssh_enable_password: "", ssh_pon_ports: 16,
  community: "public", v3_user: "", v3_auth_proto: "sha", v3_auth_key: "",
  v3_priv_proto: "aes", v3_priv_key: "", onu_count_oid: "", onu_rx_oid: "",
  onu_sn_oid: "", onu_mac_oid: "",
  rx_divisor: 1, enabled: true, snmp_password: "", device_username: "",
  device_password: "", vlan_id: "", maintenance: false, poll_interval_s: "",
  lat: "", lng: "", link_module: "", link_id: "",
};

const STATUS = {
  online: "bg-green-500/15 text-green-300 border-green-500/50",
  offline: "bg-red-500/15 text-red-300 border-red-500/50",
  unknown: "bg-slate-500/15 text-slate-300 border-slate-500/50",
};

const fmtBps = (b) => {
  b = Number(b) || 0;
  const u = ["b/s", "kb/s", "Mb/s", "Gb/s"];
  let i = 0, v = b;
  while (v >= 1000 && i < u.length - 1) { v /= 1000; i++; }
  return `${v.toFixed(i > 0 && v < 100 ? 1 : 0)} ${u[i]}`;
};

const speedToBps = (s) => {
  const m = String(s || "").match(/([\d.]+)\s*([GMk]?)/i);
  if (!m) return 0;
  return parseFloat(m[1]) * ({ g: 1e9, m: 1e6, k: 1e3, "": 1 }[m[2].toLowerCase()] || 1);
};
const utilStr = (bps, speed) => {
  const link = speedToBps(speed);
  if (!link) return null;
  const u = (Number(bps) || 0) / link * 100;
  return u < 10 ? `${u.toFixed(1)}%` : `${Math.round(u)}%`;
};

const fmtUptime = (s) => {
  s = Number(s) || 0;
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
};

export default function SNMPPanel({ role, onClose, onChanged, initialTab, initialDevice, onRequestPick, pickedCoord }) {
  const { t } = useI18n();
  const { settings } = useSettings();
  const canEdit = role === "admin" || role === "projektant";
  const canDelete = role === "admin";
  const [tab, setTab] = useState(initialTab || "devices");
  const [devices, setDevices] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | EMPTY | device
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(initialDevice || null);
  const [live, setLive] = useState(null);
  const [portHist, setPortHist] = useState({});
  const [chartPort, setChartPort] = useState(null); // {index, name}
  const [chartRange, setChartRange] = useState("1h");
  const [chartData, setChartData] = useState([]);
  const [chartBusy, setChartBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [traps, setTraps] = useState([]);
  const [olts, setOlts] = useState([]);
  const timerRef = useRef(null);

  const refreshMs = Math.max(10, Number(settings.snmp_refresh_interval) || 30) * 1000;

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/snmp/devices"); setDevices(data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadDevices();
    api.get("/snmp/vendor-profiles").then((r) => setProfiles(r.data)).catch(() => {});
    api.get("/olt").then((r) => setOlts(r.data || [])).catch(() => {});
  }, [loadDevices]);

  useEffect(() => {
    if (initialTab) setTab(initialTab);
    if (initialDevice) setSelected(initialDevice);
  }, [initialTab, initialDevice]);

  useEffect(() => {
    if (pickedCoord) setForm((f) => (f ? { ...f, lat: pickedCoord.lat, lng: pickedCoord.lng } : f));
  }, [pickedCoord]);

  const onPickOlt = (id) => {
    const o = olts.find((x) => x.id === id);
    setForm((f) => ({ ...f, link_module: id ? "olt" : "", link_id: id,
      lat: o && o.lat != null ? o.lat : f.lat, lng: o && o.lng != null ? o.lng : f.lng }));
  };

  const loadLive = useCallback(async (id) => {
    if (!id) return;
    try { const { data } = await api.get(`/snmp/devices/${id}/data`); setLive(data); } catch {}
  }, []);

  // auto-refresh live view
  useEffect(() => {
    if (tab !== "live" || !selected) { if (timerRef.current) clearInterval(timerRef.current); return; }
    loadLive(selected);
    timerRef.current = setInterval(() => loadLive(selected), refreshMs);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [tab, selected, refreshMs, loadLive]);

  // Ruch Ethernet 'na zywo': szybkie odswiezanie tabeli portow dla urzadzen SSH (~15s).
  useEffect(() => {
    if (tab !== "live" || !selected) return;
    const dev = devices.find((d) => d.id === selected);
    if (!dev || dev.poll_method !== "ssh") return;
    const load = () => api.get(`/snmp/devices/${selected}/uplinks`)
      .then((r) => {
        if (r.data && Array.isArray(r.data.ports) && r.data.ports.length) {
          setLive((prev) => (prev ? { ...prev, ports: r.data.ports } : prev));
        }
      })
      .catch(() => {});
    const loadHist = () => api.get(`/snmp/devices/${selected}/uplink-history?minutes=60`)
      .then((r) => { if (r.data) setPortHist(r.data); })
      .catch(() => {});
    load(); loadHist();
    const id = setInterval(load, 15000);
    const idh = setInterval(loadHist, 30000);
    return () => { clearInterval(id); clearInterval(idh); };
  }, [tab, selected, devices]);

  useEffect(() => {
    if (tab === "traps") api.get("/snmp/traps").then((r) => setTraps(r.data)).catch(() => {});
  }, [tab]);

  // Pelnoekranowy wykres historii portu (1h / 24h) + auto-odswiezanie co 30s.
  useEffect(() => {
    if (!chartPort || !selected) return;
    const minutes = chartRange === "24h" ? 1440 : 60;
    const load = () => {
      setChartBusy(true);
      api.get(`/snmp/devices/${selected}/uplink-history?minutes=${minutes}`)
        .then((r) => setChartData((r.data && r.data[chartPort.index]) || []))
        .catch(() => setChartData([]))
        .finally(() => setChartBusy(false));
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [chartPort, chartRange, selected]);

  const onManufacturer = (m) => {
    const p = profiles[m] || {};
    setForm((f) => ({ ...f, manufacturer: m, onu_count_oid: p.onu_count_oid ?? f.onu_count_oid,
      onu_rx_oid: p.onu_rx_oid ?? f.onu_rx_oid, rx_divisor: p.rx_divisor ?? f.rx_divisor,
      onu_sn_oid: p.onu_sn_oid ?? f.onu_sn_oid, onu_mac_oid: p.onu_mac_oid ?? f.onu_mac_oid,
      onu_status_oid: p.onu_status_oid ?? f.onu_status_oid,
      onu_status_online_value: p.onu_status_online_value ?? f.onu_status_online_value,
      onu_model_oid: p.onu_model_oid ?? f.onu_model_oid,
      onu_port_oid: p.onu_port_oid ?? f.onu_port_oid,
      onu_distance_oid: p.onu_distance_oid ?? f.onu_distance_oid }));
  };

  const submit = async () => {
    if (!form.name || !form.host) return toast.error(t("Podaj nazwę i adres IP"));
    setBusy(true);
    try {
      const body = { ...form, snmp_port: Number(form.snmp_port) || 161, rx_divisor: Number(form.rx_divisor) || 1,
        ssh_port: Number(form.ssh_port) || 22, ssh_pon_ports: Number(form.ssh_pon_ports) || 16,
        lat: form.lat === "" || form.lat == null ? null : Number(form.lat),
        lng: form.lng === "" || form.lng == null ? null : Number(form.lng) };
      if (form.version === "v2c") { body.v3_auth_key = ""; body.v3_priv_key = ""; }
      if (form.id) await api.put(`/snmp/devices/${form.id}`, body);
      else await api.post("/snmp/devices", body);
      toast.success(t("Zapisano urządzenie"));
      setForm(null); loadDevices(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("Usunąć to urządzenie SNMP?"))) return;
    try { await api.delete(`/snmp/devices/${id}`); toast.success(t("Usunięto")); loadDevices(); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pollNow = async (id) => {
    setPolling(true);
    try {
      const { data } = await api.post(`/snmp/devices/${id}/poll`);
      if (data.status === "online") toast.success(t("Odpytano urządzenie"));
      else toast.error(`${t("Urządzenie nie odpowiada")}: ${data.error || ""}`);
      if (selected === id) setLive(data);
      loadDevices(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPolling(false); }
  };

  const [pingBusy, setPingBusy] = useState(null);
  const pingDevice = async (id) => {
    setPingBusy(id);
    try {
      const { data } = await api.post(`/ping/device/${id}`);
      if (data.alive) toast.success(`${t("Ping OK")}${data.rtt_ms != null ? ` · ${data.rtt_ms} ms` : ""}`);
      else toast.error(`${t("Brak odpowiedzi")}${data.error ? ` · ${data.error}` : ""}`);
      loadDevices(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPingBusy(null); }
  };
  const openEdit = (d) => setForm({ ...EMPTY, ...d, community: "", v3_auth_key: "", v3_priv_key: "", snmp_password: "", device_password: "" });
  const inputCls = "w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500";
  const selDevice = devices.find((d) => d.id === selected);

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="snmp-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
        <div className="flex items-center gap-2"><Router size={18} className="text-cyan-400" />
          <h2 className="font-heading font-bold text-lg">{t("Monitoring SNMP")}</h2></div>
        <button onClick={onClose} data-testid="snmp-close" className="p-1.5 rounded hover:bg-slate-700"><X size={18} /></button>
      </div>

      <div className="flex items-center gap-1 px-4 pt-3 border-b border-slate-800">
        {[["devices", t("Urządzenia"), Router], ["live", t("Podgląd na żywo"), Activity], ["traps", t("Trapy"), Bell]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`snmp-tab-${k}`}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-md border-b-2 transition-colors ${tab === k ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div className="overflow-y-auto p-5 space-y-4 flex-1">
        {tab === "devices" && (
          <>
            {canEdit && !form && (
              <button onClick={() => setForm({ ...EMPTY })} data-testid="snmp-add-device"
                className="w-full bg-cyan-600 hover:bg-cyan-500 rounded py-2 text-sm flex items-center justify-center gap-2">
                <Plus size={15} /> {t("Dodaj urządzenie")}</button>
            )}

            {form && (
              <div className="bg-slate-800/60 rounded-lg p-4 border border-slate-700 space-y-2" data-testid="snmp-device-form">
                <h3 className="font-heading font-bold text-sm mb-1">{form.id ? t("Edycja urządzenia") : t("Nowe urządzenie")}</h3>
                <div className="grid grid-cols-2 gap-2">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("Nazwa (np. OLT Rynek)")} data-testid="snmp-f-name" className={inputCls} />
                  <input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder={t("Adres IP / host")} data-testid="snmp-f-host" className={inputCls} />
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Metoda odczytu")}</label>
                    <select value={form.poll_method} onChange={(e) => setForm({ ...form, poll_method: e.target.value })} data-testid="snmp-f-method" className={inputCls}>
                      <option value="snmp">SNMP</option>
                      <option value="ssh">SSH (CLI) — VSOL/C-Data EPON</option>
                      <option value="ping">Ping (ICMP/TCP)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Producent")}</label>
                    <select value={form.manufacturer} onChange={(e) => onManufacturer(e.target.value)} data-testid="snmp-f-vendor" className={inputCls}>
                      {Object.keys(profiles).length === 0 && <option value="generic">generic</option>}
                      {Object.keys(profiles).map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                </div>

                {form.poll_method !== "ssh" && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Port SNMP")}</label>
                    <input type="number" value={form.snmp_port} onChange={(e) => setForm({ ...form, snmp_port: e.target.value })} data-testid="snmp-f-port" className={inputCls} />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Wersja SNMP")}</label>
                    <select value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="snmp-f-version" className={inputCls}>
                      <option value="v2c">v2c</option><option value="v3">v3</option>
                    </select>
                  </div>
                  {form.version === "v2c" && (
                    <div className="col-span-2">
                      <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Community")} {form.community_set && <span className="text-green-400">{t("(ustawione)")}</span>}</label>
                      <input value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} placeholder={form.community_set ? "•••• (puste = bez zmian)" : "public"} data-testid="snmp-f-community" className={inputCls} autoComplete="off" />
                    </div>
                  )}
                </div>
                )}

                {form.poll_method === "ssh" && (
                  <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-700 mt-1" data-testid="snmp-ssh-block">
                    <div className="col-span-2 text-[11px] text-slate-400">{t("Dostęp SSH — tylko odczyt (komendy „show”, bez zapisu). Zalecane dla VSOL/C-Data EPON: pełny odczyt Rx/Tx/temperatury/dystansu ONU.")}</div>
                    <input value={form.ssh_user} onChange={(e) => setForm({ ...form, ssh_user: e.target.value })} placeholder={t("Login SSH (np. admin)")} data-testid="snmp-f-sshuser" className={inputCls} autoComplete="off" />
                    <div>
                      <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Port SSH")}</label>
                      <input type="number" value={form.ssh_port} onChange={(e) => setForm({ ...form, ssh_port: e.target.value })} data-testid="snmp-f-sshport" className={inputCls} />
                    </div>
                    <input type="password" value={form.ssh_password} onChange={(e) => setForm({ ...form, ssh_password: e.target.value })} placeholder={form.ssh_password_set ? "•••• (puste = bez zmian)" : t("Hasło SSH")} data-testid="snmp-f-sshpass" className={inputCls} autoComplete="off" />
                    <input type="password" value={form.ssh_enable_password} onChange={(e) => setForm({ ...form, ssh_enable_password: e.target.value })} placeholder={form.ssh_enable_password_set ? "•••• enable (puste = bez zmian)" : t("Hasło enable (opcjonalnie)")} data-testid="snmp-f-sshenable" className={inputCls} autoComplete="off" />
                    <div className="col-span-2">
                      <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Liczba portów PON (skan 0/1…0/N)")}</label>
                      <input type="number" value={form.ssh_pon_ports} onChange={(e) => setForm({ ...form, ssh_pon_ports: e.target.value })} data-testid="snmp-f-sshpon" className={inputCls} />
                    </div>
                  </div>
                )}

                {form.poll_method !== "ssh" && form.version === "v3" && (
                  <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-700 mt-1">
                    <div className="col-span-2 text-[11px] text-slate-400">{t("Poświadczenia SNMPv3 (uwierzytelnianie + szyfrowanie)")}</div>
                    <input value={form.v3_user} onChange={(e) => setForm({ ...form, v3_user: e.target.value })} placeholder={t("Użytkownik v3")} data-testid="snmp-f-v3user" className={inputCls} autoComplete="off" />
                    <div />
                    <select value={form.v3_auth_proto} onChange={(e) => setForm({ ...form, v3_auth_proto: e.target.value })} data-testid="snmp-f-authproto" className={inputCls}>
                      <option value="sha">Auth: SHA</option><option value="md5">Auth: MD5</option>
                    </select>
                    <input type="password" value={form.v3_auth_key} onChange={(e) => setForm({ ...form, v3_auth_key: e.target.value })} placeholder={form.v3_auth_key_set ? "•••• (puste = bez zmian)" : t("Klucz Auth")} data-testid="snmp-f-authkey" className={inputCls} autoComplete="off" />
                    <select value={form.v3_priv_proto} onChange={(e) => setForm({ ...form, v3_priv_proto: e.target.value })} data-testid="snmp-f-privproto" className={inputCls}>
                      <option value="aes">Priv: AES</option><option value="des">Priv: DES</option>
                    </select>
                    <input type="password" value={form.v3_priv_key} onChange={(e) => setForm({ ...form, v3_priv_key: e.target.value })} placeholder={form.v3_priv_key_set ? "•••• (puste = bez zmian)" : t("Klucz Priv")} data-testid="snmp-f-privkey" className={inputCls} autoComplete="off" />
                  </div>
                )}

                {form.poll_method !== "ssh" && (
                <div className="grid grid-cols-3 gap-2 pt-1 border-t border-slate-700 mt-1">
                  <div className="col-span-3 text-[11px] text-slate-400">{profiles[form.manufacturer]?.note || t("OID-y PON/ONU zależą od modelu i firmware — zweryfikuj.")}</div>
                  <input value={form.onu_count_oid} onChange={(e) => setForm({ ...form, onu_count_oid: e.target.value })} placeholder={t("OID liczby ONU")} data-testid="snmp-f-onucount" className={`${inputCls} col-span-3`} />
                  <input value={form.onu_rx_oid} onChange={(e) => setForm({ ...form, onu_rx_oid: e.target.value })} placeholder={t("OID Rx ONU")} data-testid="snmp-f-onurx" className={`${inputCls} col-span-2`} />
                  <input type="number" value={form.rx_divisor} onChange={(e) => setForm({ ...form, rx_divisor: e.target.value })} placeholder={t("Dzielnik Rx")} data-testid="snmp-f-rxdiv" className={inputCls} />
                  <input value={form.onu_sn_oid} onChange={(e) => setForm({ ...form, onu_sn_oid: e.target.value })} placeholder={t("OID S/N ONU")} data-testid="snmp-f-onusn" className={`${inputCls} col-span-3`} />
                  <input value={form.onu_mac_oid} onChange={(e) => setForm({ ...form, onu_mac_oid: e.target.value })} placeholder={t("OID MAC ONU")} data-testid="snmp-f-onumac" className={`${inputCls} col-span-3`} />
                </div>
                )}

                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-700 mt-1">
                  <div className="col-span-2 text-[11px] text-slate-400">{t("Dostęp do urządzenia (dokumentacja) i sieć")}</div>
                  <input type="password" value={form.snmp_password} onChange={(e) => setForm({ ...form, snmp_password: e.target.value })} placeholder={form.snmp_password_set ? t("Hasło SNMP") + " ••••" : t("Hasło SNMP")} data-testid="snmp-f-snmppass" className={inputCls} autoComplete="off" />
                  <input value={form.device_username} onChange={(e) => setForm({ ...form, device_username: e.target.value })} placeholder={t("Login do urządzenia")} data-testid="snmp-f-devuser" className={inputCls} autoComplete="off" />
                  <input type="password" value={form.device_password} onChange={(e) => setForm({ ...form, device_password: e.target.value })} placeholder={form.device_password_set ? t("Hasło do urządzenia (dostęp)") + " ••••" : t("Hasło do urządzenia (dostęp)")} data-testid="snmp-f-devpass" className={inputCls} autoComplete="off" />
                  <input type="number" value={form.vlan_id} onChange={(e) => setForm({ ...form, vlan_id: e.target.value })} placeholder={t("VLAN ID (opcjonalnie)")} data-testid="snmp-f-vlan" className={inputCls} />
                </div>

                <div className="grid grid-cols-3 gap-2 pt-1 border-t border-slate-700 mt-1">
                  <div className="col-span-3 text-[11px] text-slate-400">{t("Lokalizacja na mapie (dziedziczona z OLT lub ręczna)")}</div>
                  <select value={form.link_id || ""} onChange={(e) => onPickOlt(e.target.value)} data-testid="snmp-f-olt" className={`${inputCls} col-span-3`}>
                    <option value="">{t("— powiąż z OLT (dziedziczy lokalizację) —")}</option>
                    {olts.map((o) => <option key={o.id} value={o.id}>{o.name || o.number || o.id}</option>)}
                  </select>
                  <input type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder={t("Szerokość (lat)")} data-testid="snmp-f-lat" className={inputCls} />
                  <input type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} placeholder={t("Długość (lng)")} data-testid="snmp-f-lng" className={inputCls} />
                  <button type="button" onClick={() => onRequestPick && onRequestPick()} data-testid="snmp-f-pickmap"
                    className="col-span-3 bg-slate-700 hover:bg-slate-600 rounded px-2 py-2 text-xs flex items-center justify-center gap-1.5"><MapPin size={13} /> {t("Wskaż na mapie")}</button>
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer pt-1" data-testid="snmp-f-maintenance-label">
                  <input type="checkbox" checked={form.maintenance} onChange={(e) => setForm({ ...form, maintenance: e.target.checked })} data-testid="snmp-f-maintenance" className="accent-amber-500" />
                  <span className="text-amber-300">{t("Tryb serwisowy (ignoruj alarmy)")}</span>
                </label>

                {form.poll_method === "ping" && (
                  <div className="grid grid-cols-2 gap-2 pt-1" data-testid="snmp-ping-threshold-block">
                    <div>
                      <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Po ilu nieudanych pingach = awaria")}</label>
                      <input type="number" min="1" value={form.ping_fail_threshold ?? ""} onChange={(e) => setForm({ ...form, ping_fail_threshold: e.target.value })} placeholder="3" data-testid="snmp-f-ping-threshold" className={inputCls} />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-400 mb-0.5 block">{t("Co ile sprawdzać [s] (puste = ciągły)")}</label>
                      <input type="number" min="5" value={form.poll_interval_s ?? ""} onChange={(e) => setForm({ ...form, poll_interval_s: e.target.value })} placeholder={t("globalny")} data-testid="snmp-f-ping-interval" className={inputCls} />
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
                  <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="snmp-f-enabled" className="accent-cyan-500" />
                  {t("Aktywne (odpytywanie automatyczne)")}
                </label>

                <div className="flex gap-2 pt-1">
                  <button onClick={submit} disabled={busy} data-testid="snmp-f-save" className="flex-1 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 rounded py-2 text-sm flex items-center justify-center gap-2">
                    {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {t("Zapisz")}</button>
                  <button onClick={() => setForm(null)} data-testid="snmp-f-cancel" className="bg-slate-700 hover:bg-slate-600 rounded py-2 px-4 text-sm">{t("Anuluj")}</button>
                </div>
              </div>
            )}

            {loading ? <div className="text-slate-500 text-sm">{t("Ładowanie…")}</div> : devices.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-8">{t("Brak urządzeń. Dodaj pierwsze urządzenie SNMP.")}</div>
            ) : (
              <div className="space-y-2" data-testid="snmp-device-list">
                {devices.map((d) => (
                  <div key={d.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid={`snmp-row-${d.id}`}>
                    <div className="flex items-center gap-2">
                      {d.last_status === "online" ? <Wifi size={16} className="text-green-400" /> : d.last_status === "offline" ? <WifiOff size={16} className="text-red-400" /> : <Router size={16} className="text-slate-400" />}
                      <span className="font-semibold text-slate-200">{d.name}</span>
                      <span className="text-xs text-slate-500 font-mono">{d.host}:{d.poll_method === "ssh" ? (d.ssh_port || 22) : d.snmp_port}</span>
                      <span className="text-[10px] uppercase text-cyan-300 border border-cyan-500/40 rounded px-1.5 py-0.5">{d.poll_method === "ssh" ? "SSH" : d.version} · {d.manufacturer}</span>
                      {d.vlan_id ? <span className="text-[10px] text-slate-300 border border-slate-600 rounded px-1.5 py-0.5">VLAN {d.vlan_id}</span> : null}
                      {d.maintenance ? <span className="text-[10px] uppercase text-amber-300 border border-amber-500/50 rounded px-1.5 py-0.5" data-testid={`snmp-maint-${d.id}`}>{t("Serwis")}</span> : null}
                      <span className={`text-[10px] rounded px-1.5 py-0.5 border ml-auto ${STATUS[d.last_status] || STATUS.unknown}`}>{t(d.last_status || "unknown")}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      <button onClick={() => { setSelected(d.id); setTab("live"); }} data-testid={`snmp-view-${d.id}`}
                        className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1"><Activity size={13} /> {t("Podgląd")}</button>
                      <button onClick={() => pollNow(d.id)} disabled={polling} data-testid={`snmp-poll-${d.id}`}
                        className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1 disabled:opacity-60">{polling ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} {t("Odpytaj teraz")}</button>
                      <button onClick={() => pingDevice(d.id)} data-testid={`snmp-ping-${d.id}`}
                        className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1">{pingBusy === d.id ? <Loader2 size={13} className="animate-spin" /> : <Radar size={13} />} Ping</button>
                      {canEdit && <button onClick={() => openEdit(d)} data-testid={`snmp-edit-${d.id}`} className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2 py-1 flex items-center gap-1"><Pencil size={13} /></button>}
                      {canDelete && <button onClick={() => del(d.id)} data-testid={`snmp-del-${d.id}`} className="text-xs bg-red-900/40 hover:bg-red-800/50 text-red-300 rounded px-2 py-1 flex items-center gap-1"><Trash2 size={13} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "live" && (
          <div className="space-y-3" data-testid="snmp-live">
            <div className="flex items-center gap-2">
              <select value={selected || ""} onChange={(e) => setSelected(e.target.value)} data-testid="snmp-live-select" className={inputCls}>
                <option value="">{t("— wybierz urządzenie —")}</option>
                {devices.map((d) => <option key={d.id} value={d.id}>{`${d.name} (${d.host})`}</option>)}
              </select>
              {selected && <button onClick={() => pollNow(selected)} disabled={polling} data-testid="snmp-live-poll" className="shrink-0 bg-cyan-600 hover:bg-cyan-500 rounded px-3 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60">{polling ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {t("Odpytaj teraz")}</button>}
            </div>
            <p className="text-[11px] text-slate-500">{t("Auto-odświeżanie co")} {refreshMs / 1000}s ({t("konfigurowalne w Ustawieniach")}).</p>

            {!selected ? <div className="text-slate-500 text-sm text-center py-8">{t("Wybierz urządzenie, aby zobaczyć metryki.")}</div> : !live ? (
              <div className="text-slate-500 text-sm">{t("Brak danych — kliknij „Odpytaj teraz\".")}</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                    <div className="text-[11px] text-slate-400">{t("Status")}</div>
                    <div className={`text-sm font-bold ${live.status === "online" ? "text-green-400" : "text-red-400"}`}>{t(live.status)}</div>
                  </div>
                  <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                    <div className="text-[11px] text-slate-400">{t("Czas pracy")}</div>
                    <div className="text-sm font-bold text-slate-200">{fmtUptime(live.uptime_seconds)}</div>
                  </div>
                  <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                    <div className="text-[11px] text-slate-400">{t("ONU online / offline")}</div>
                    <div className="text-sm font-bold" data-testid="snmp-onu-counts">
                      <span className="text-green-400">{live.onu_online != null ? live.onu_online : (live.pon || []).reduce((s, p) => s + (Number(p.onu_online) || 0), 0)}</span>
                      <span className="text-slate-500"> / </span>
                      <span className="text-red-400">{live.onu_offline != null ? live.onu_offline : 0}</span>
                    </div>
                  </div>
                </div>
                {live.sysdescr && <div className="text-[11px] text-slate-500 break-words">{live.sysdescr}</div>}
                {live.error && <div className="text-[11px] text-amber-400">⚠ {live.error}</div>}

                {(live.pon || []).length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5"><Router size={13} /> {t("Porty PON")}</h4>
                    <div className="flex flex-wrap gap-2">
                      {live.pon.map((p, i) => <span key={i} className="text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1">PON {p.port}: <b className="text-cyan-300">{p.onu_online}</b> ONU</span>)}
                    </div>
                  </div>
                )}

                {(live.onu_rx || []).length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5"><Signal size={13} /> {t("Wykryte ONU")} <span className="text-slate-500 font-normal">({live.onu_rx.length}{live.onu_online != null && <> · <span className="text-green-400">{t("online")}: {live.onu_online}</span> · <span className="text-red-400">{t("offline")}: {live.onu_offline != null ? live.onu_offline : 0}</span></>})</span></h4>
                    <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="snmp-onu-list">
                      {live.onu_rx.map((r, i) => (
                        <span key={i} title={`${r.port != null ? "PON " + r.port + "  " : ""}${r.model ? "Model: " + r.model + "  " : ""}${r.sn ? "S/N: " + r.sn + "  " : ""}${r.onu_type ? "Typ: " + r.onu_type + "  " : ""}${r.mac ? "MAC: " + r.mac : ""}${r.distance_m != null ? "  Dystans: " + r.distance_m + " m" : ""}${r.rx_dbm != null ? "  Rx: " + r.rx_dbm + " dBm" : ""}${r.tx_dbm != null ? "  Tx: " + r.tx_dbm + " dBm" : ""}${r.temp_c != null ? "  Temp: " + r.temp_c + "°C" : ""}${r.status ? "  " + r.status : ""}`}
                          className={`text-xs rounded px-2 py-1 border truncate ${r.status === "offline" ? "bg-red-500/10 text-red-300 border-red-500/40" : r.status === "online" ? (r.rx_dbm != null && r.rx_dbm < -28 ? "bg-amber-500/10 text-amber-300 border-amber-500/40" : "bg-green-500/10 text-green-300 border-green-500/40") : "bg-slate-800 border-slate-700"}`}>
                          {r.port != null ? `PON${r.port} · ` : ""}#{r.index}{r.mac ? ` · ${r.mac}` : ""}{r.distance_m != null ? ` · ${r.distance_m}m` : ""}{r.rx_dbm != null ? ` · ${r.rx_dbm}dBm` : ""}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="text-xs font-bold text-slate-300 mb-1 flex items-center gap-1.5"><ArrowDownUp size={13} /> {t("Ruch na portach")}</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="snmp-ports-table">
                      <thead><tr className="text-slate-400 text-left border-b border-slate-700">
                        <th className="py-1.5 pr-2">{t("Port")}</th><th className="py-1.5 pr-2">{t("Status")}</th>
                        <th className="py-1.5 pr-2">{t("Prędkość")}</th>
                        <th className="py-1.5 pr-2 text-right">↓ {t("Pobieranie")}</th><th className="py-1.5 pr-2 text-right">↑ {t("Wysyłanie")}</th>
                        <th className="py-1.5 text-right">{t("Historia (1h)")}</th>
                      </tr></thead>
                      <tbody>
                        {(live.ports || []).map((p) => (
                          <tr key={p.index} className="border-b border-slate-800/60">
                            <td className="py-1 pr-2 text-slate-300">{p.name}</td>
                            <td className="py-1 pr-2"><span className={p.oper_status === "up" ? "text-green-400" : "text-slate-500"}>{p.oper_status}</span></td>
                            <td className="py-1 pr-2 text-slate-300 font-mono">{p.speed || "—"}</td>
                            <td className="py-1 pr-2 text-right font-mono text-cyan-300">{fmtBps(p.in_bps)}{utilStr(p.in_bps, p.speed) && <span className="text-slate-500"> ({utilStr(p.in_bps, p.speed)})</span>}</td>
                            <td className="py-1 pr-2 text-right font-mono text-pink-300">{fmtBps(p.out_bps)}{utilStr(p.out_bps, p.speed) && <span className="text-slate-500"> ({utilStr(p.out_bps, p.speed)})</span>}</td>
                            <td className="py-1 text-right">
                              {(portHist[p.index] || []).length > 1 ? (
                                <LineChart width={110} height={30} data={portHist[p.index]} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                                  <Line type="monotone" dataKey="in_bps" stroke="#22d3ee" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                                  <Line type="monotone" dataKey="out_bps" stroke="#f472b6" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                                </LineChart>
                              ) : <span className="text-slate-600 text-[10px]">{t("zbieranie…")}</span>}
                            </td>
                          </tr>
                        ))}
                        {(live.ports || []).length === 0 && <tr><td colSpan={6} className="py-3 text-center text-slate-500">{t("Brak danych o portach")}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "traps" && (
          <div className="space-y-2" data-testid="snmp-traps">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-400">{t("Ostatnie odebrane trapy (UDP)")} · {selDevice ? "" : ""}</p>
              <button onClick={() => api.get("/snmp/traps").then((r) => setTraps(r.data))} data-testid="snmp-traps-refresh" className="text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1 flex items-center gap-1"><RefreshCw size={13} /> {t("Odśwież")}</button>
            </div>
            {traps.length === 0 ? <div className="text-slate-500 text-sm text-center py-8">{t("Brak odebranych trapów.")}</div> : (
              traps.map((tr, i) => (
                <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3" data-testid={`snmp-trap-${i}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <Bell size={13} className="text-amber-400" />
                    <span className="font-mono text-slate-300">{tr.source}</span>
                    <span className="text-slate-500">{tr.version}</span>
                    <span className="ml-auto text-slate-500">{String(tr.ts).slice(0, 19).replace("T", " ")}</span>
                  </div>
                  {(tr.varbinds || []).length > 0 && (
                    <div className="mt-1.5 space-y-0.5">
                      {tr.varbinds.map((vb, j) => (
                        <div key={j} className="text-[11px] font-mono text-slate-400 break-all"><span className="text-cyan-400">{vb.oid}</span> = {vb.value}</div>
                      ))}
                    </div>
                  )}
                  {tr.error && <div className="text-[11px] text-amber-400 mt-1">{tr.error}</div>}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {chartPort && (
        <div className="fixed inset-0 z-[1500] bg-black/70 flex items-center justify-center p-4" data-testid="port-chart-modal"
          onClick={() => setChartPort(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-cyan-300">{t("Historia ruchu")} · {chartPort.name}</h3>
                {chartPort.speed && <span className="text-xs text-slate-500">{t("Prędkość")}: {chartPort.speed}</span>}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex bg-slate-800 rounded-lg overflow-hidden border border-slate-700">
                  {["1h", "24h"].map((r) => (
                    <button key={r} onClick={() => setChartRange(r)} data-testid={`port-chart-range-${r}`}
                      className={`px-3 py-1.5 text-xs font-medium ${chartRange === r ? "bg-cyan-600 text-white" : "text-slate-300 hover:bg-slate-700"}`}>{r}</button>
                  ))}
                </div>
                <button onClick={() => setChartPort(null)} data-testid="port-chart-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
              </div>
            </div>
            <div className="h-[360px]">
              {chartData.length > 1 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="ts" tickFormatter={(v) => String(v).slice(11, 16)} stroke="#64748b" fontSize={11} minTickGap={40} />
                    <YAxis tickFormatter={(v) => fmtBps(v)} stroke="#64748b" fontSize={11} width={70} />
                    <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
                      labelFormatter={(v) => String(v).slice(0, 19).replace("T", " ")}
                      formatter={(val, name) => [fmtBps(val), name === "in_bps" ? t("Pobieranie") : t("Wysyłanie")]} />
                    <Legend formatter={(v) => v === "in_bps" ? `↓ ${t("Pobieranie")}` : `↑ ${t("Wysyłanie")}`} />
                    <Line type="monotone" dataKey="in_bps" stroke="#22d3ee" strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="out_bps" stroke="#f472b6" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500 text-sm">
                  {chartBusy ? <Loader2 className="animate-spin" size={20} /> : t("Za mało danych w tym zakresie — zbieranie…")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
